import { apiFetch } from '../lib/api';
import type { ProductEdition } from '../lib/editions';

export type { ProductEdition };

/**
 * Типизированные вызовы API. Один слой поверх apiFetch, чтобы типы ответов
 * жили в одном месте и переиспользовались нативными клиентами в Phase 2.
 */

export type ProductType = 'KEY' | 'ACCOUNT' | 'LICENSE' | 'SUBSCRIPTION' | 'TEXT';
export type OrderStatus = 'PENDING' | 'AWAITING_PAYMENT' | 'PAID' | 'DELIVERED' | 'CANCELLED' | 'FAILED' | 'REFUNDED';

export interface Category {
  id: string;
  slug: string;
  title: string;
  iconUrl: string | null;
}

export type CatalogKind = 'keys' | 'subscriptions' | 'services' | 'deals';

export interface ProductCard {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  type: ProductType;
  priceStars: number;
  compareAtStars: number | null;
  priceRub: number | null;
  compareAtRub: number | null;
  coverUrl: string | null;
  maxPerOrder: number;
  stockQuantity?: number | null;
  hasTribute?: boolean;
  isTopUp?: boolean;
  topUpMinRub?: number | null;
  topUpMaxRub?: number | null;
  commissionBps?: number;
  editions?: ProductEdition[];
  category: { id: string; slug: string; title: string } | null;
  availableCount: number;
  inStock: boolean;
  ratingAvg?: number | null;
  ratingCount?: number;
}

export interface Paginated<T> {
  items: T[];
  nextCursor?: string;
}

export type PaymentProvider = 'TELEGRAM_STARS' | 'TRIBUTE' | 'APPLE_IAP' | 'GOOGLE_PLAY_BILLING' | 'INTERNAL_BALANCE';
export type PaymentKind = 'stars-invoice' | 'tribute-webapp' | 'native-product';
export type CheckoutPlatform = 'telegram' | 'tribute';

export interface PaymentOptions {
  tribute: boolean;
  stars: boolean;
  minorPerStar?: number;
}

export interface OrderSummary {
  id: string;
  number: string;
  status: OrderStatus;
  totalAmount: number;
  currency: string;
  paymentProvider: PaymentProvider | null;
  paymentKind: PaymentKind;
  createdAt: string;
  deliveredAt: string | null;
  invoiceUrl: string | null;
  expiresAt: string | null;
  items: Array<{
    id: string;
    title: string;
    unitPrice: number;
    refundedAt?: string | null;
    canReview?: boolean;
    review?: ProductReview | null;
    product: { id: string; slug: string; title: string; coverUrl: string | null; type: ProductType } | null;
  }>;
}

export interface CheckoutResponse {
  orderId: string;
  orderNumber: string;
  totalAmount: number;
  currency: string;
  expiresAt: string | null;
  provider: PaymentProvider;
  paymentKind: PaymentKind;
  invoiceUrl?: string;
  nativeProductId?: string;
}

export interface OrderSecrets {
  orderId: string;
  number: string;
  deliveredAt: string | null;
  items: Array<{
    id: string;
    title: string;
    type: ProductType;
    instructions: string | null;
    secret: string | null;
  }>;
}

export interface ProductReview {
  id: string;
  productId: string;
  rating: number;
  body: string | null;
  author: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProductReviews {
  productId: string;
  ratingAvg: number | null;
  ratingCount: number;
  canReview: boolean;
  myReview: ProductReview | null;
  items: ProductReview[];
}

export interface AdminReview extends ProductReview {
  hiddenAt: string | null;
  user: { id: string; username: string | null; displayName: string | null };
  product: { id: string; title: string; slug: string };
}

export interface Profile {
  id: string;
  role: 'USER' | 'ADMIN';
  displayName: string | null;
  username: string | null;
  avatarUrl: string | null;
  starBalance: number;
  createdAt: string;
  ordersCount: number;
}

export type InventoryStatus = 'AVAILABLE' | 'RESERVED' | 'SOLD' | 'DISABLED';

/** Разбивка стока по статусам — то, по чему видно, что пора докупать ключи. */
export type StockBreakdown = Record<InventoryStatus, number>;

export interface AdminProduct {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  type: ProductType;
  priceStars: number;
  compareAtStars: number | null;
  priceRub: number | null;
  compareAtRub: number | null;
  coverUrl: string | null;
  deliveryInstructions: string | null;
  isActive: boolean;
  maxPerOrder: number;
  stockQuantity: number | null;
  stockListed: boolean;
  tributeUrl: string | null;
  isTopUp: boolean;
  topUpMinRub: number | null;
  topUpMaxRub: number | null;
  commissionBps: number;
  editions?: ProductEdition[];
  category: { slug: string; title: string } | null;
  stock: StockBreakdown;
}

export interface AdminCategory {
  id: string;
  slug: string;
  title: string;
  sortOrder: number;
  isActive: boolean;
  _count: { products: number };
}

export interface AdminUser {
  id: string;
  role: 'USER' | 'ADMIN';
  displayName: string | null;
  username: string | null;
  avatarUrl?: string | null;
  languageCode?: string | null;
  starBalance: number;
  isBlocked: boolean;
  createdAt: string;
  telegramId?: string | null;
  lastLoginAt?: string | null;
  spentStars?: number;
  paidOrders?: number;
  lastOrderAt?: string | null;
  _count: { orders: number };
}

export interface AdminUserDetails extends AdminUser {
  orders: AdminOrder[];
}

export interface AdminStats {
  users: number;
  products: { total: number; active: number };
  stock: Partial<Record<InventoryStatus, number>>;
  orders: Partial<Record<OrderStatus, number>>;
  stars: { earned: number; refunded: number; net: number };
  reviews?: number;
}

export interface AdminOrderItem {
  id: string;
  titleSnapshot: string;
  unitPrice: number;
  refundedAt: string | null;
  payloadHintSnapshot: string | null;
  inventoryItem: { id: string; payloadHint: string | null; status: InventoryStatus } | null;
}

export interface AdminOrder {
  id: string;
  number: string;
  status: OrderStatus;
  totalAmount: number;
  paymentProvider?: string | null;
  cancelReason?: string | null;
  createdAt: string;
  user: { id: string; displayName: string | null; username: string | null };
  items: AdminOrderItem[];
}

export interface NewProduct {
  slug: string;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  type?: ProductType;
  categorySlug?: string | null;
  priceStars: number;
  compareAtStars?: number | null;
  priceRub?: number | null;
  compareAtRub?: number | null;
  coverUrl?: string | null;
  deliveryInstructions?: string | null;
  maxPerOrder?: number;
  stockQuantity?: number | null;
  stockListed?: boolean;
  tributeUrl?: string | null;
  isTopUp?: boolean;
  topUpMinRub?: number | null;
  topUpMaxRub?: number | null;
  commissionBps?: number;
  editions?: Array<{
    id?: string;
    title: string;
    description?: string | null;
    priceStars: number;
    compareAtStars?: number | null;
    priceRub?: number | null;
    compareAtRub?: number | null;
  }>;
}

export interface AdminStockItem {
  id: string;
  status: InventoryStatus;
  payloadHint: string | null;
  reservedByOrderId: string | null;
  reservedUntil: string | null;
  soldAt: string | null;
  createdAt: string;
}

export const marketplaceApi = {
  categories: () => apiFetch<{ items: Category[] }>('/catalog/categories', { auth: false }),

  products: (
    params: {
      category?: string;
      kind?: CatalogKind;
      search?: string;
      sort?: 'newest' | 'oldest' | 'price_asc' | 'price_desc' | 'title';
      cursor?: string;
      take?: number;
    } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.category) query.set('category', params.category);
    if (params.kind) query.set('kind', params.kind);
    if (params.search) query.set('search', params.search);
    if (params.sort) query.set('sort', params.sort);
    if (params.cursor) query.set('cursor', params.cursor);
    if (params.take) query.set('take', String(params.take));

    const queryString = query.toString();
    return apiFetch<Paginated<ProductCard>>(
      `/catalog/products${queryString ? `?${queryString}` : ''}`,
      { auth: false },
    );
  },

  product: (idOrSlug: string) => apiFetch<ProductCard>(`/catalog/products/${idOrSlug}`, { auth: false }),

  payments: () => apiFetch<PaymentOptions>('/catalog/payments', { auth: false }),

  checkout: (
    items: Array<{ productId: string; quantity: number; topUpAmount?: number; editionId?: string }>,
    platform: CheckoutPlatform = 'telegram',
  ) =>
    apiFetch<CheckoutResponse>('/orders', {
      method: 'POST',
      body: { items, platform },
    }),

  orders: () => apiFetch<Paginated<OrderSummary>>('/orders'),

  order: (orderId: string) => apiFetch<OrderSummary>(`/orders/${orderId}`),

  secrets: (orderId: string) => apiFetch<OrderSecrets>(`/orders/${orderId}/secrets`),

  cancelOrder: (orderId: string) => apiFetch<{ ok: true }>(`/orders/${orderId}/cancel`, { method: 'POST' }),

  reviews: (productId: string) => apiFetch<ProductReviews>(`/catalog/products/${productId}/reviews`),

  saveReview: (input: { productId: string; rating: number; body?: string | null; orderId?: string }) =>
    apiFetch<ProductReview>('/reviews', { method: 'POST', body: input }),

  me: () => apiFetch<Profile>('/auth/me'),
};

const TERMINAL_STATUSES: OrderStatus[] = ['DELIVERED', 'FAILED', 'CANCELLED', 'REFUNDED'];

/** Tribute не даёт колбэк openInvoice — ждём вебхук, опрашивая заказ. */
export async function pollOrderUntilSettled(
  orderId: string,
  timeoutMs = 3 * 60 * 1000,
): Promise<OrderSummary> {
  const started = Date.now();

  while (Date.now() - started < timeoutMs) {
    const order = await marketplaceApi.order(orderId);

    if (TERMINAL_STATUSES.includes(order.status)) {
      return order;
    }

    await new Promise((resolve) => setTimeout(resolve, document.visibilityState === 'visible' ? 2000 : 4000));
  }

  return marketplaceApi.order(orderId);
}

/**
 * Админские вызовы. Отдельным объектом, а не полем в marketplaceApi: так видно,
 * что эти методы доступны не всем, и их нельзя случайно дёрнуть из страницы
 * покупателя.
 */
export const adminApi = {
  stats: () => apiFetch<AdminStats>('/admin/stats'),

  users: () => apiFetch<{ items: AdminUser[] }>('/admin/users'),

  user: (id: string) => apiFetch<AdminUserDetails>(`/admin/users/${id}`),

  categories: () => apiFetch<{ items: AdminCategory[] }>('/admin/categories'),

  createCategory: (input: { slug: string; title: string; sortOrder?: number }) =>
    apiFetch<AdminCategory>('/admin/categories', { method: 'POST', body: input }),

  products: () => apiFetch<{ items: AdminProduct[] }>('/admin/products'),

  createProduct: (input: NewProduct) => apiFetch<AdminProduct>('/admin/products', { method: 'POST', body: input }),

  updateProduct: (id: string, patch: Partial<NewProduct> & { isActive?: boolean }) =>
    apiFetch<AdminProduct>(`/admin/products/${id}`, { method: 'PATCH', body: patch }),

  deleteProduct: (id: string) => apiFetch<{ ok: true }>(`/admin/products/${id}`, { method: 'DELETE' }),

  uploadCover: (dataUrl: string) => apiFetch<{ url: string }>('/admin/uploads', { method: 'POST', body: { dataUrl } }),

  addStock: (productId: string, secrets: string[]) =>
    apiFetch<{ added: number; duplicates: number }>(`/admin/products/${productId}/stock`, {
      method: 'POST',
      body: { secrets },
    }),

  stock: (productId: string) => apiFetch<{ items: AdminStockItem[] }>(`/admin/products/${productId}/stock`),

  replaceKey: (id: string, secret: string) =>
    apiFetch<{ id: string; payloadHint: string | null; status: InventoryStatus }>(`/admin/inventory/${id}`, {
      method: 'PATCH',
      body: { secret },
    }),

  deleteKey: (id: string) => apiFetch<{ ok: true }>(`/admin/inventory/${id}`, { method: 'DELETE' }),

  disableKey: (id: string) => apiFetch<{ id: string; status: InventoryStatus }>(`/admin/inventory/${id}/disable`, { method: 'POST' }),

  orders: (status?: OrderStatus) =>
    apiFetch<Paginated<AdminOrder>>(`/admin/orders${status ? `?status=${status}` : ''}`),

  refunds: () => apiFetch<{ pending: AdminOrder[]; done: AdminOrder[] }>('/admin/refunds'),

  refund: (orderId: string, reason: string, itemIds?: string[]) =>
    apiFetch<{ orderId: string; refunded: number; restocked: number; moneyNote: string }>(
      `/admin/orders/${orderId}/refund`,
      {
        method: 'POST',
        body: { reason, ...(itemIds?.length ? { itemIds } : {}) },
      },
    ),

  reviews: () => apiFetch<{ items: AdminReview[] }>('/admin/reviews'),

  hideReview: (id: string, hidden: boolean) =>
    apiFetch<AdminReview>(`/admin/reviews/${id}`, { method: 'PATCH', body: { hidden } }),

  deleteReview: (id: string) => apiFetch<{ ok: true }>(`/admin/reviews/${id}`, { method: 'DELETE' }),
};
